import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import PublicLayout from "@/components/PublicLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Gift, Sparkles, Wand2 } from "lucide-react";

const PRESETS = [10, 25, 50, 100];
const MIN_AMOUNT = 5;
const MAX_AMOUNT = 500;

export default function GiftCard() {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(25);
  const [customMode, setCustomMode] = useState(false);
  const [form, setForm] = useState({
    buyer_email: "", buyer_name: "",
    recipient_email: "", recipient_name: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const setAmountValidated = (v) => {
    const n = parseFloat(v);
    if (Number.isNaN(n)) return;
    setAmount(Math.max(MIN_AMOUNT, Math.min(MAX_AMOUNT, Math.round(n * 100) / 100)));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.buyer_email || !form.recipient_email || !form.buyer_name || !form.recipient_name) {
      return toast.error(t("gift.err_required"));
    }
    if (amount < MIN_AMOUNT || amount > MAX_AMOUNT) {
      return toast.error(`Import ${MIN_AMOUNT}–${MAX_AMOUNT} €`);
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/gift-card/checkout", {
        amount, ...form, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || t("gift.err_checkout"));
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-2 lg:sticky lg:top-24">
            <div className="overline text-accent">{t("gift.overline")}</div>
            <h1 className="mt-2 text-3xl sm:text-4xl font-heading font-bold tracking-tight">{t("gift.title")}</h1>
            <p className="mt-4 text-muted-foreground leading-relaxed">{t("gift.subtitle")}</p>
            <div className="mt-8 relative rounded-3xl overflow-hidden aspect-[4/5] border-2 border-border/60 shadow-xl shadow-primary/10">
              <img src="https://images.pexels.com/photos/34972183/pexels-photo-34972183.jpeg?w=600" alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-primary/20 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-primary-foreground">
                <div className="text-xs uppercase tracking-widest opacity-90">Shopping Salou</div>
                <div className="font-heading font-bold text-3xl">{amount.toFixed(2)} €</div>
                <div className="text-xs opacity-80 mt-1">{t("gift.valid_12")}</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <Card className="rounded-3xl border-2">
              <CardContent className="p-6 sm:p-8">
                <form onSubmit={submit} className="space-y-8" data-testid="giftcard-form">
                  <div>
                    <Label className="text-base font-heading">{t("gift.choose_amount")}</Label>
                    <div className="grid grid-cols-5 gap-2 mt-3">
                      {PRESETS.map((a) => (
                        <button
                          key={a}
                          type="button"
                          onClick={() => { setCustomMode(false); setAmount(a); }}
                          data-testid={`giftcard-amount-${a}`}
                          className={`py-4 rounded-2xl border-2 font-heading font-bold text-base sm:text-lg transition-colors duration-200 ${
                            !customMode && amount === a ? "border-accent bg-accent/10 text-primary" : "border-border hover:border-accent/60"
                          }`}
                        >
                          {a} €
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => setCustomMode(true)}
                        data-testid="giftcard-amount-custom"
                        className={`py-4 rounded-2xl border-2 font-heading font-bold text-xs sm:text-sm transition-colors duration-200 flex flex-col items-center justify-center gap-1 ${
                          customMode ? "border-accent bg-accent/10 text-primary" : "border-border hover:border-accent/60"
                        }`}
                      >
                        <Wand2 className="w-4 h-4" aria-hidden="true" />
                        Lliure
                      </button>
                    </div>
                    {customMode && (
                      <div className="mt-3 flex items-center gap-3" data-testid="giftcard-custom-wrapper">
                        <Input
                          type="number"
                          min={MIN_AMOUNT}
                          max={MAX_AMOUNT}
                          step="0.01"
                          value={amount}
                          onChange={(e) => setAmountValidated(e.target.value)}
                          onBlur={() => setAmount(Number(amount.toFixed(2)))}
                          className="rounded-xl text-lg font-heading font-bold h-14"
                          data-testid="giftcard-custom-amount"
                          aria-label={t("gift.choose_amount")}
                        />
                        <span className="text-lg font-heading font-bold text-muted-foreground">€</span>
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground mt-2">Import lliure entre {MIN_AMOUNT} i {MAX_AMOUNT} €.</div>
                  </div>

                  <div>
                    <div className="text-sm font-heading font-semibold mb-3 flex items-center gap-2">
                      <Gift className="w-4 h-4 text-accent" /> {t("gift.buyer")}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Input placeholder={t("gift.buyer_name_ph")} aria-label={t("gift.buyer_name_ph")} required value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} className="rounded-xl" data-testid="giftcard-buyer-name" />
                      <Input type="email" placeholder={t("gift.buyer_email_ph")} aria-label={t("gift.buyer_email_ph")} required value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} className="rounded-xl" data-testid="giftcard-buyer-email" />
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-heading font-semibold mb-3 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-accent" /> {t("gift.recipient")}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Input placeholder={t("gift.recipient_name_ph")} aria-label={t("gift.recipient_name_ph")} required value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className="rounded-xl" data-testid="giftcard-recipient-name" />
                      <Input type="email" placeholder={t("gift.recipient_email_ph")} aria-label={t("gift.recipient_email_ph")} required value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} className="rounded-xl" data-testid="giftcard-recipient-email" />
                    </div>
                  </div>

                  <div>
                    <Label>{t("gift.message")}</Label>
                    <Textarea className="mt-2 rounded-xl min-h-24" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t("gift.message_ph")} aria-label={t("gift.message")} data-testid="giftcard-message" />
                  </div>

                  <Button type="submit" size="lg" disabled={loading} className="w-full rounded-full h-12" data-testid="giftcard-submit-btn">
                    {loading ? t("gift.redirecting") : `${t("gift.buy")} · ${amount.toFixed(2)} €`}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    {t("gift.secure_note")}
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
